import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { ApiService } from "../../shared/services/api.service";

export interface DispatcherCity {
  _id: string;
  name: string;
  countryId: string;
  location?: { coordinates: [number, number] };
  timezone?: string;
}

const LS_KEY = "dispatcher.cityId";

@Injectable({ providedIn: "root" })
export class DispatcherScopeService {
  private citiesSub = new BehaviorSubject<DispatcherCity[]>([]);
  private selectedSub = new BehaviorSubject<DispatcherCity | null>(null);
  private countryIdValue: string | null = null;
  /**
   * `{ [cityId]: BusinessType[] }` — the taxi-family businesses each city has switched on
   * for the dispatcher, from City Settings. Booking screens must narrow their business
   * selector to this; the dispatcher's own `entity.businessTypes` is an account-level
   * permission and says nothing about what a given city actually runs.
   */
  private businessTypesByCity: Record<string, number[]> = {};

  cities$: Observable<DispatcherCity[]> = this.citiesSub.asObservable();
  selectedCity$: Observable<DispatcherCity | null> = this.selectedSub.asObservable();

  constructor(private apiService: ApiService) {}

  load(): Observable<DispatcherCity[]> {
    return new Observable<DispatcherCity[]>((obs) => {
      this.apiService.get({ url: "/api/dispatcher/me/cities", parameters: {} }).subscribe({
        next: (res: any) => {
          const cities: DispatcherCity[] = res?.data?.data?.cities || res?.data?.cities || [];
          this.countryIdValue = res?.data?.data?.countryId || res?.data?.countryId || null;
          this.businessTypesByCity = res?.data?.data?.businessTypesByCity || res?.data?.businessTypesByCity || {};
          this.citiesSub.next(cities);
          const stored = localStorage.getItem(LS_KEY);
          const initial = cities.find((c) => c._id === stored) || cities[0] || null;
          this.selectedSub.next(initial);
          if (initial) localStorage.setItem(LS_KEY, initial._id);
          obs.next(cities);
          obs.complete();
        },
        error: (err: any) => obs.error(err)
      });
    });
  }

  setCity(cityId: string): void {
    const target = this.citiesSub.value.find((c) => c._id === cityId);
    if (!target || target._id === this.currentCityId()) return;
    this.selectedSub.next(target);
    localStorage.setItem(LS_KEY, target._id);
  }

  /** Snapshot of the dispatcher's cities — for callers that need a plain list
   *  rather than a subscription (e.g. a "message by city" dropdown). */
  cities(): DispatcherCity[] {
    return this.citiesSub.value;
  }

  currentCity(): DispatcherCity | null {
    return this.selectedSub.value;
  }

  currentCityId(): string | null {
    return this.selectedSub.value?._id ?? null;
  }

  currentCountryId(): string | null {
    return this.selectedSub.value?.countryId ?? this.countryIdValue ?? null;
  }

  /**
   * Business types the selected city has enabled for this dispatcher.
   *
   * Returns `null` when the mapping isn't known yet (or the server predates this field),
   * which callers must treat as "don't filter" — hiding every business type because a
   * response hasn't landed would leave the booking screen unusable.
   */
  currentCityBusinessTypes(): number[] | null {
    const cityId = this.currentCityId();
    if (!cityId) return null;
    const list = this.businessTypesByCity[cityId];
    return Array.isArray(list) ? list : null;
  }

  clear(): void {
    this.citiesSub.next([]);
    this.selectedSub.next(null);
    this.countryIdValue = null;
    this.businessTypesByCity = {};
    localStorage.removeItem(LS_KEY);
  }
}
